import React, { useEffect } from 'react';

const VARIANTS = {
    danger: {
        iconBg: 'from-red-500 to-rose-600',
        iconShadow: 'shadow-rose-500/20',
        confirmBtn: 'bg-red-500 hover:bg-red-600 text-white',
        icon: 'delete',
    },
    warning: {
        iconBg: 'from-amber-500 to-orange-600',
        iconShadow: 'shadow-orange-500/20',
        confirmBtn: 'bg-amber-500 hover:bg-amber-600 text-white',
        icon: 'warning',
    },
    info: {
        iconBg: 'from-blue-500 to-indigo-600',
        iconShadow: 'shadow-indigo-500/20',
        confirmBtn: 'bg-primary hover:bg-indigo-600 text-white',
        icon: 'info',
    },
};

export default function ConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title = 'Are you sure?',
    message = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'danger',
    busy = false,
    busyLabel = 'Processing…',
}) {
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            if (e.key === 'Escape' && !busy) onClose?.();
            if (e.key === 'Enter' && !busy) onConfirm?.();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, busy, onClose, onConfirm]);

    if (!isOpen) return null;

    const v = VARIANTS[variant] || VARIANTS.danger;

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-[confirmFadeIn_0.18s_ease-out]"
                onClick={() => !busy && onClose?.()}
            />
            <div className="relative w-full max-w-sm mx-4 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col animate-[confirmSlideUp_0.22s_ease-out]">
                <div className="flex items-start gap-3 px-5 pt-5 pb-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${v.iconBg} flex items-center justify-center shadow-lg ${v.iconShadow} shrink-0`}>
                        <span className="material-symbols-outlined text-white text-[20px]">{v.icon}</span>
                    </div>
                    <div className="flex-1 pt-0.5">
                        <h3 className="text-sm font-bold text-slate-800 dark:text-white">{title}</h3>
                        {message && (
                            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{message}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-3.5 py-1.5 rounded-lg text-[12px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className={`px-3.5 py-1.5 rounded-lg text-[12px] font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5 ${v.confirmBtn}`}
                    >
                        {busy && <span className="material-symbols-outlined animate-spin text-[14px]">progress_activity</span>}
                        {busy ? busyLabel : confirmLabel}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes confirmFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes confirmSlideUp {
                    from { opacity: 0; transform: translateY(12px) scale(0.97); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </div>
    );
}
